import { AlertTriangle, ArrowLeft, Plus } from "lucide-react";
import React, { useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { useNavigate } from "react-router";
import { RequireScope } from "src/components/RequireScope";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { MarkdownEditorLazy as MarkdownEditor } from "src/components/ui/markdown-editor-lazy";
import { useMe } from "src/context/MeContext";
import { useTeamSelection } from "src/hooks/use-team-selection";
import { useToast } from "src/hooks/use-toast";
import AddKnowledgeResourceModal from "src/pages/KnowledgePage/components/AddKnowledgeResourceModal";
import { getKnowledgeFileName, getProductName } from "src/utils/brandTheme";

import { useSettingsMobileHeader } from "../hooks/useSettingsMobileHeader";
import { CreateTeamRouteCreateTeamMutation } from "./__generated__/CreateTeamRouteCreateTeamMutation.graphql";
import { CreateTeamRouteQuery } from "./__generated__/CreateTeamRouteQuery.graphql";
import { CreateTeamRouteUpdateInstructionMutation } from "./__generated__/CreateTeamRouteUpdateInstructionMutation.graphql";

const createTeamRouteQuery = graphql`
  query CreateTeamRouteQuery {
    me {
      ...appMe
      id
      teamContext {
        id
        team {
          id
          name
        }
      }
      organization {
        id
        integrations {
          edges {
            node {
              id
              integrationType
            }
          }
        }
      }
    }
  }
`;

const createTeamMutation = graphql`
  mutation CreateTeamRouteCreateTeamMutation($input: CreateTeamInput!) {
    createTeam(input: $input) {
      team {
        id
        name
        description
      }
    }
  }
`;

const updateInstructionMutation = graphql`
  mutation CreateTeamRouteUpdateInstructionMutation($body: String!, $teamId: GlobalID) {
    updateInstruction(body: $body, teamId: $teamId) {
      success {
        instruction {
          id
          body
        }
      }
      error {
        message
      }
    }
  }
`;

export default function CreateTeamRoute() {
  return (
    <RequireScope scope="write:teams">
      <CreateTeamRouteContent />
    </RequireScope>
  );
}

function CreateTeamRouteContent() {
  useSettingsMobileHeader("Create Team");
  const navigate = useNavigate();
  const { toast } = useToast();
  const me = useMe();

  const data = useLazyLoadQuery<CreateTeamRouteQuery>(createTeamRouteQuery, {});
  const [commitCreateTeam, isCreatingTeam] = useMutation<CreateTeamRouteCreateTeamMutation>(createTeamMutation);
  const [commitUpdateInstruction, isUpdatingInstruction] = useMutation<CreateTeamRouteUpdateInstructionMutation>(updateInstructionMutation);

  // Get team selection hook for switching context after creation
  const userId = data.me?.id || "";
  const { selectTeam } = useTeamSelection({ userId, initialTeamContext: data.me?.teamContext });

  // Form state
  const [teamName, setTeamName] = useState("");
  const [teamDescription, setTeamDescription] = useState("");
  const [teamInstructions, setTeamInstructions] = useState("");
  const [isResourceModalOpen, setIsResourceModalOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [errorLine, setErrorLine] = useState<number | null>(null);

  const brandTheme = me.organization?.brandTheme ?? "TIERZERO";
  const productName = getProductName(brandTheme);

  // Map integrations for AddKnowledgeResourceModal
  const integrationsList = (data.me?.organization?.integrations?.edges ?? [])
    .filter(edge => edge?.node)
    .map(edge => ({
      id: edge!.node!.id,
      name: edge!.node!.integrationType || "",
      type: edge!.node!.integrationType || "",
      status: "connected" as const,
    }));

  const validateMarkdown = (content: string): { error: string; line: number } | null => {
    if (!content.trim()) return null;
    const lines = content.split("\n");
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.match(/^#{1,2}\s+/)) {
        return {
          error: `Line ${i + 1}: Headers must use ### or more. Single # and ## are not allowed.`,
          line: i + 1
        };
      }
    }
    return null;
  };

  const handleInstructionsChange = (value: string) => {
    setTeamInstructions(value);
    setValidationError(null);
    setErrorLine(null);
  };

  const handleCreateTeam = useCallback(() => {
    if (!teamName.trim()) {
      toast({
        title: "Error",
        description: "Team name is required",
        variant: "error",
      });
      return;
    }

    // Validate markdown if provided
    if (teamInstructions.trim()) {
      const validation = validateMarkdown(teamInstructions);
      if (validation) {
        setValidationError(validation.error);
        setErrorLine(validation.line);
        return;
      }
    }

    commitCreateTeam({
      variables: {
        input: {
          name: teamName.trim(),
          description: teamDescription.trim() || null,
        },
      },
      updater: (store, data) => {
        if (!data) return;
        const newTeam = data.createTeam?.team;
        if (newTeam) {
          const root = store.getRoot();
          const me = root.getLinkedRecord("me");
          const organization = me?.getLinkedRecord("organization");
          const teams = organization?.getLinkedRecord("teams");
          const edges = teams?.getLinkedRecords("edges") || [];

          // Create a new edge for the new team
          const newEdge = store.create(
            `client:newEdge:${newTeam.id}`,
            "GraphQLTeamEdge"
          );
          const teamRecord = store.get(newTeam.id);
          if (teamRecord) {
            newEdge.setLinkedRecord(teamRecord, "node");
          }

          // Add the new edge to the existing edges
          if (teams) {
            teams.setLinkedRecords([...edges, newEdge], "edges");
          }
        }
      },
      onCompleted: (response) => {
        const newTeam = response.createTeam?.team;
        if (newTeam) {
          // If there are team instructions, save them
          if (teamInstructions.trim()) {
            commitUpdateInstruction({
              variables: {
                body: teamInstructions,
                teamId: newTeam.id,
              },
              onCompleted: (updateResponse) => {
                if (updateResponse.updateInstruction?.error) {
                  toast({
                    title: "Warning",
                    description: `Team created but failed to save instructions: ${updateResponse.updateInstruction.error.message}`,
                    variant: "error",
                  });
                } else {
                  toast({
                    title: "Success",
                    description: `Team "${newTeam.name}" created successfully`,
                  });
                }
                // Switch to the new team and navigate back
                selectTeam(newTeam.id, newTeam.name).then(() => {
                  // Small delay to ensure backend has persisted before reload
                  setTimeout(() => {
                    window.location.href = "/settings/teams";
                  }, 100);
                });
              },
              onError: (error) => {
                toast({
                  title: "Warning",
                  description: `Team created but failed to save instructions: ${error.message}`,
                  variant: "error",
                });
                selectTeam(newTeam.id, newTeam.name).then(() => {
                  // Small delay to ensure backend has persisted before reload
                  setTimeout(() => {
                    window.location.href = "/settings/teams";
                  }, 100);
                });
              },
            });
          } else {
            toast({
              title: "Success",
              description: `Team "${newTeam.name}" created successfully`,
            });
            // Switch to the new team and navigate back
            selectTeam(newTeam.id, newTeam.name).then(() => {
              // Small delay to ensure backend has persisted before reload
              setTimeout(() => {
                window.location.href = "/settings/teams";
              }, 100);
            });
          }
        }
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "error",
        });
      },
    });
  }, [teamName, teamDescription, teamInstructions, commitCreateTeam, commitUpdateInstruction, selectTeam, navigate, toast]);

  const isLoading = isCreatingTeam || isUpdatingInstruction;

  const placeholder = `Guidelines:
- Format instructions as direct statements of context or desired agent behaviors
- Be specific about any entity names or identifiers
- Use ### and #### for headers (# and ## are not allowed)

Include any quirks, debugging tips, or special information needed for investigating this team's resources.

Example:

### About
- A brief description of the team and its responsibilities

### Services Owned
- Services this team owns and is responsible for

### Debugging Tips
- Common patterns, gotchas, or tips for debugging this team's services

### Known Issues
- Known issues and baseline patterns that ${productName} should be aware of`;

  return (
    <div className="flex-1 px-4 md:px-5 pt-4 pb-5">
      {/* Header with back button */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/settings/teams")}
          className="p-2 hover:bg-accent rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-muted-foreground" />
        </button>
        <h1 className="text-xl font-semibold text-foreground">Create Team</h1>
      </div>

      <div className="max-w-4xl space-y-6">
        {/* Basic Info Section */}
        <div className="bg-card/30 rounded-lg p-6 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="team-name">Team Name <span className="text-red-500">*</span></Label>
            <Input
              id="team-name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Enter team name"
              className="max-w-md"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="team-description">Description</Label>
            <p className="text-muted-foreground text-sm">
              Describe what this team handles. {productName} uses this to decide when to route requests to this team.
            </p>
            <MarkdownEditor
              value={teamDescription}
              onChange={setTeamDescription}
              placeholder="E.g., Handles payment processing, billing issues, and subscription management"
              minHeight="360px"
              autoFocus={false}
            />
          </div>
        </div>

        {/* Team Instructions Section */}
        <div className="bg-card/30 rounded-lg p-6 space-y-4">
          <div>
            <Label>Team Instructions</Label>
            <p className="text-muted-foreground text-sm mt-1">
              Add team-specific context and instructions that aren't in the global instructions file.
              Include any quirks, debugging tips, or special information needed for investigating this team's resources.
            </p>
          </div>

          {/* Warning about experimental generation */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-amber-200 text-sm">
                <span className="font-medium">Tip:</span> You can always edit these instructions later under {getKnowledgeFileName(brandTheme)} by selecting this team from the sidebar.
              </p>
            </div>
          </div>

          <div className="relative">
            {validationError && (
              <div className="absolute -top-1 right-0 text-xs text-red-400 z-10">
                {validationError}
              </div>
            )}
            <MarkdownEditor
              value={teamInstructions}
              onChange={handleInstructionsChange}
              height="300px"
              minHeight="200px"
              error={!!validationError}
              errorLine={errorLine ?? undefined}
              placeholder={placeholder}
            />
          </div>
        </div>

        {/* Resources Section */}
        <div className="bg-card/30 rounded-lg p-6 space-y-4">
          <div>
            <Label>Team Resources</Label>
            <p className="text-muted-foreground text-sm mt-1">
              Optionally add dashboards, runbooks, or documentation specific to this team.
              {productName} will learn from these resources when investigating issues for this team.
            </p>
          </div>

          <Button
            variant="outline"
            onClick={() => setIsResourceModalOpen(true)}
            disabled={integrationsList.length === 0}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Resource
          </Button>

          {integrationsList.length === 0 && (
            <p className="text-muted-foreground text-sm">
              Connect integrations in Settings → Integrations to add resources.
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => navigate("/settings/teams")}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateTeam}
            disabled={isLoading || !teamName.trim()}
            loading={isLoading}
            loadingText="Creating..."
          >
            Create Team
          </Button>
        </div>
      </div>

      <AddKnowledgeResourceModal
        open={isResourceModalOpen}
        onClose={() => setIsResourceModalOpen(false)}
        onSuccess={() => setIsResourceModalOpen(false)}
        integrations={integrationsList}
      />
    </div>
  );
}
